import toast from "react-hot-toast";
import { useMutation } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import {
  pdfFilename,
  withReadableErrorBody,
} from "features/patients/utils/patientRecordPdf";
import { getServerErrorMessage } from "utils/showMutationError";

const saveBlob = (data, filename) => {
  const blobUrl = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = blobUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

/**
 * Downloads one of the patient's PDFs from `pdf/patientreport/patient/{patientId}`.
 *
 * `suffix` picks the document: "" is the patient report, "/documents" the letters and
 * documents bundle (KIN-2207). A failure says the server's own reason, for example the
 * 403 sentence for a missing "Export full patient record" grant, and says it once: the
 * global MutationCache toast is switched off here.
 */
export const usePatientPdfDownload = ({
  suffix = "",
  fallbackFilename,
  failureMessage,
}) =>
  useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async ({ tenantVal, patientId }) => {
      const axiosInstance = createAxiosInstance();

      try {
        const response = await axiosInstance.get(
          `${tenantVal}/pdf/patientreport/patient/${patientId}${suffix}`,
          { responseType: "blob" }
        );

        saveBlob(
          response.data,
          pdfFilename(response.headers, fallbackFilename(patientId))
        );
      } catch (error) {
        throw await withReadableErrorBody(error);
      }
    },
    onError: (error) =>
      toast.error(getServerErrorMessage(error, failureMessage)),
  });
